/** Builder question types supported in campaign-service draft schema. */
export type SurveyQuestionType =
  | 'yes_no'
  | 'multiple_choice'
  | 'checkbox'
  | 'dropdown'
  | 'picture_choice'
  | 'ranking'
  | 'short_text'
  | 'long_text'
  | 'date'
  | 'rating'
  | 'likert'
  | 'slider'
  | 'video'
  | 'unsupported'

export const BATCH1_SURVEY_QUESTION_TYPES: readonly SurveyQuestionType[] = [
  'yes_no',
  'multiple_choice',
  'checkbox',
  'dropdown',
  'rating',
  'likert',
  'slider',
  'ranking',
  'picture_choice',
  'short_text',
  'long_text',
  'date',
  'video',
] as const

export type SurveyRatingScaleMax = 5 | 10
export type SurveyRatingDisplayStyle = 'stars' | 'numbers'
export type SurveyLikertPointCount = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10
export type SurveyImageSize = 'small' | 'medium' | 'large'
export type SurveyDateInputFormat = 'dd/MM/yyyy' | 'MM/dd/yyyy'

export function isBatch1SurveyQuestionType(
  type: string,
): type is (typeof BATCH1_SURVEY_QUESTION_TYPES)[number] {
  return type !== 'unsupported' && (BATCH1_SURVEY_QUESTION_TYPES as readonly string[]).includes(type)
}

export interface SurveyOption {
  id: string
  label: string
  /** Ranking / picture-choice — optional respondent image. */
  imageUrl: string | null
}

export interface SurveyQuestion {
  id: string
  order: number
  type: SurveyQuestionType
  prompt: string
  description: string | null
  mandatory: boolean
  options: SurveyOption[]
  yesLabel: string
  noLabel: string
  minSelections: number | null
  maxSelections: number | null
  /** Dropdown placeholder (Figma 5074-13310); falls back to a generic label when null. */
  placeholder: string | null
  /** Rating scale length (campaign `ratingScaleMax`). */
  ratingScaleMax: SurveyRatingScaleMax
  ratingDisplayStyle: SurveyRatingDisplayStyle
  ratingLowLabel: string
  ratingHighLabel: string
  likertPointCount: SurveyLikertPointCount
  likertLabels: string[]
  likertLayoutHorizontal: boolean
  sliderMinValue: number
  sliderMaxValue: number
  sliderStep: number
  sliderLowLabel: string
  sliderHighLabel: string
  /** Ranking / picture-choice display size from campaign draft. */
  imageSize: SurveyImageSize
  /** Short / long text / video — optional character bounds from campaign draft. */
  minLength: number | null
  maxLength: number | null
  /** Video — YouTube / Vimeo / MP4 URL or authenticated serve path. */
  videoUrl?: string | null
  /** Date — respondent display format (campaign `dateInputFormat`). */
  dateInputFormat: SurveyDateInputFormat
  earliestSelectableDate: string | null
  latestSelectableDate: string | null
  disablePastDates: boolean
  disableFutureDates: boolean
  randomizeOrder: boolean
  layoutHorizontal: boolean
  multiColumn: boolean
  yesNoToggleStyle: boolean
  hasOtherOption: boolean
  hasNoneOfAboveOption: boolean
  /**
   * BFP-1156 — option that reveals a required specify field (Multiple Choice / Checkbox).
   * `undefined` = legacy question saved before Add Text (Other keeps its always-on field).
   */
  textResponse?: SurveyChoiceTextResponse | null
  /**
   * Multi-select option id → routing destination (`end` or `q:<questionId>`).
   * Options that map to different destinations cannot be selected together.
   * Options absent from the map have no skip/branch and stay compatible.
   */
  routingOptionDestinations?: Readonly<Record<string, string>>
}

export interface SurveyChoiceTextResponse {
  target: 'option' | 'other' | 'none'
  optionId?: string
  minCharacters: number | null
  maxCharacters: number | null
}

export interface SurveyDefinition {
  instrumentId: string
  campaignId: string
  /** Platform tenant id from responses/start definition (BFP-1119). */
  tenantId?: string
  /** Tenant DNS label (`tenantDomainPrefix` on API). */
  tenantDomainPrefix?: string
  questions: SurveyQuestion[]
}

/** Choice questions with Other — local state before save. */
export interface ChoiceDraftAnswer {
  selectedIds: string[]
  otherText: string
}

/** Local draft value before PATCH — mirrors save payload shapes. */
export type SurveyDraftAnswer = string | string[] | number | ChoiceDraftAnswer | null
